import { useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  CheckCircleIcon,
  Cluster,
  Grid,
  Page,
  QueryState,
  Stack,
  Stat,
  StatStrip,
  DocumentIcon,
  LayersIcon,
  TrendIcon,
  cx,
  type Tone,
} from '@integra/ui';
import type { PlanCatalogueEntry, PlanSummary, PlanTierName } from '../api/types.js';
import { enterpriseSalesEmail } from '../lib/salesContact.js';
import { useOpenBillingPortal, useStartCheckout, useUsage } from '../api/hooks.js';

/**
 * Billing (§2.5, §11.4). Where a workspace sees its plan, how much of it it is
 * using, this period's projected bill, and — self-serve — subscribes or moves
 * tier through Stripe Checkout, or manages the subscription in the Customer
 * Portal. Everything shown is the org's REAL state from the usage endpoint.
 */

const TIER_TONE: Record<PlanTierName, Tone> = {
  bronze: 'warning',
  silver: 'neutral',
  gold: 'gold',
};

const TIER_ORDER: Record<PlanTierName, number> = { bronze: 0, silver: 1, gold: 2 };

function money(pence: number): string {
  return `£${(pence / 100).toFixed(pence % 100 === 0 ? 0 : 2)}`;
}

function limitLabel(value: number | null): string {
  return value === null ? 'Unlimited' : value.toLocaleString();
}

/**
 * A usage-vs-limit bullet meter. Deliberately NOT the shared `Progress` primitive
 * (§21.4): Progress reports a task-completion percentage, whereas this reports
 * capacity against a reference — the IBCS bullet-chart register. It shows the
 * absolute `used / limit`, a goal tick at the reference line, a variance readout
 * ("N left" / "+N over"), and near-cap/overage warning colour.
 *
 * `kind` separates the two meanings: a hard `cap` (channels/assets — the cap IS
 * the end of the track, no overage possible) from an `included` allowance
 * (publishes/renders — overage is billed not blocked, so the bar runs PAST the
 * goal tick to show it). Unlimited shows a calm, non-filling meter.
 */
function UsageBar({
  label,
  used,
  limit,
  kind = 'cap',
}: {
  label: string;
  used: number;
  limit: number | null;
  kind?: 'cap' | 'included';
}) {
  if (limit === null) {
    return (
      <div>
        <div className="mb-1 flex items-baseline justify-between text-xs">
          <span className="text-ink-muted">{label}</span>
          <span className="font-mono tabular-nums text-navy-700">
            {used.toLocaleString()} <span className="text-ink-muted">/ Unlimited</span>
          </span>
        </div>
        <div className="h-2.5 overflow-hidden rounded-full bg-surface-sunken">
          <div className="h-full rounded-full bg-navy-200" style={{ width: '6%' }} />
        </div>
      </div>
    );
  }

  const over = used > limit;
  const near = used / Math.max(1, limit) >= 0.8;
  // Bullet scale: an included allowance extends past the goal so overage reads
  // beyond the tick; a hard cap ends exactly at the cap.
  const max = kind === 'included' ? Math.max(used, limit) * 1.08 : limit;
  const pct = Math.min(100, Math.round((used / Math.max(1, max)) * 100));
  const tickPct = Math.min(100, Math.round((limit / Math.max(1, max)) * 100));
  const fill = over || near ? 'bg-state-warning' : 'bg-gold-400';
  const remaining = limit - used;
  const variance =
    kind === 'included' && over
      ? { text: `+${(used - limit).toLocaleString()} over`, tone: 'text-state-warning' }
      : remaining <= 0
        ? { text: 'At capacity', tone: 'text-state-warning' }
        : { text: `${remaining.toLocaleString()} left`, tone: 'text-ink-muted' };

  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-3 text-xs">
        <span className="text-ink-muted">{label}</span>
        <span className="flex items-baseline gap-2.5">
          <span className={cx('font-mono tabular-nums', variance.tone)}>{variance.text}</span>
          <span className="font-mono tabular-nums text-navy-700">
            {used.toLocaleString()}
            <span className="text-ink-muted"> / {limit.toLocaleString()}</span>
          </span>
        </span>
      </div>
      <div className="relative h-2.5 overflow-hidden rounded-full bg-surface-sunken">
        <div className={cx('h-full rounded-full', fill)} style={{ width: `${String(pct)}%` }} />
        {kind === 'included' && (
          // The goal line (included allowance) — a data-driven position, the same
          // sanctioned inline style the fill uses for width (§21.4).
          <span
            className="absolute inset-y-0 w-0.5 bg-navy-700"
            style={{ left: `${String(tickPct)}%` }}
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  );
}

function PlanCard({
  entry,
  current,
  onChoose,
  busy,
}: {
  entry: PlanCatalogueEntry;
  current: PlanTierName;
  onChoose: () => void;
  busy: boolean;
}) {
  const isCurrent = entry.tier === current;
  const isDowngrade = TIER_ORDER[entry.tier] < TIER_ORDER[current];
  return (
    <Card
      title={entry.tier.charAt(0).toUpperCase() + entry.tier.slice(1)}
      actions={<Badge tone={TIER_TONE[entry.tier]}>{money(entry.pricing.baseMonthly)}/mo</Badge>}
      {...(isCurrent ? { className: 'ring-2 ring-gold-400' } : {})}
    >
      <Stack gap="sm">
        <dl className="space-y-1 text-xs">
          <Cluster gap="sm">
            <dt className="text-ink-muted">Channels</dt>
            <dd className="text-navy-700">{limitLabel(entry.limits.channels)}</dd>
          </Cluster>
          <Cluster gap="sm">
            <dt className="text-ink-muted">Assets</dt>
            <dd className="text-navy-700">{limitLabel(entry.limits.assets)}</dd>
          </Cluster>
          <Cluster gap="sm">
            <dt className="text-ink-muted">Publishes / mo</dt>
            <dd className="text-navy-700">
              {entry.pricing.included.publishes.toLocaleString()} incl.
            </dd>
          </Cluster>
          <Cluster gap="sm">
            <dt className="text-ink-muted">Renders / mo</dt>
            <dd className="text-navy-700">
              {entry.pricing.included.renders.toLocaleString()} incl.
            </dd>
          </Cluster>
        </dl>
        {isCurrent ? (
          <Badge tone="success" dot>
            Current plan
          </Badge>
        ) : (
          <Button
            variant={isDowngrade ? 'secondary' : 'primary'}
            size="sm"
            onClick={onChoose}
            loading={busy}
          >
            {isDowngrade ? `Move to ${entry.tier}` : `Upgrade to ${entry.tier}`}
          </Button>
        )}
      </Stack>
    </Card>
  );
}

/**
 * The bespoke top tier. Enterprise is not self-serve — no fixed price and no
 * Stripe checkout; an agency with custom needs (volume, procurement, custom SLA)
 * talks to the enterprise team, who provisions it. Rendered as the natural fourth
 * tier beside the self-serve catalogue so the ladder is complete.
 */
const ENTERPRISE_FEATURES = [
  'SSO & SCIM provisioning',
  'White-label branding & domain',
  'Dedicated success manager',
  'Custom SLA, contract & invoicing',
  'Priority publishing & rendering',
];

function EnterpriseCard() {
  const salesEmail = enterpriseSalesEmail();
  return (
    <Card title="Enterprise" actions={<Badge tone="neutral">Custom</Badge>}>
      <Stack gap="sm">
        <dl className="space-y-1 text-xs">
          <Cluster gap="sm">
            <dt className="text-ink-muted">Channels</dt>
            <dd className="text-navy-700">Unlimited</dd>
          </Cluster>
          <Cluster gap="sm">
            <dt className="text-ink-muted">Assets</dt>
            <dd className="text-navy-700">Unlimited</dd>
          </Cluster>
          <Cluster gap="sm">
            <dt className="text-ink-muted">Publishes / mo</dt>
            <dd className="text-navy-700">Unlimited</dd>
          </Cluster>
          <Cluster gap="sm">
            <dt className="text-ink-muted">Renders / mo</dt>
            <dd className="text-navy-700">Unlimited</dd>
          </Cluster>
        </dl>
        <ul className="space-y-1 text-[11px] text-ink-muted">
          {ENTERPRISE_FEATURES.map((f) => (
            <li key={f} className="flex items-start gap-1.5">
              <span className="mt-0.5 shrink-0 text-gold-600">
                <CheckCircleIcon size={12} />
              </span>
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            window.location.href = `mailto:${salesEmail}?subject=${encodeURIComponent(
              'INTEGRA Enterprise enquiry',
            )}`;
          }}
        >
          Talk to sales
        </Button>
        <p className="text-center text-[11px] text-ink-muted">{salesEmail}</p>
      </Stack>
    </Card>
  );
}

function CurrentPlan({ plan, workspaceId }: { plan: PlanSummary; workspaceId: string }) {
  const portal = useOpenBillingPortal(workspaceId);
  const openPortal = (): void => {
    portal.mutate(undefined, {
      onSuccess: ({ url }) => {
        globalThis.location.href = url;
      },
    });
  };
  return (
    <Card
      title="Your plan"
      subtitle={`Billing period ${plan.bill.currency} · base ${money(plan.bill.base)}/mo`}
      actions={
        <Cluster gap="sm">
          <Badge tone={TIER_TONE[plan.tier]}>{plan.tier}</Badge>
          <Button variant="secondary" size="sm" onClick={openPortal} loading={portal.isPending}>
            Manage subscription
          </Button>
        </Cluster>
      }
    >
      <Stack gap="md">
        <Grid min="sm" gap="sm">
          <Stat
            label="This period"
            value={money(plan.bill.total)}
            provenance="measured"
            hint="Base + metered overage"
          />
          <Stat label="Overage" value={money(plan.bill.overageTotal)} provenance="measured" />
        </Grid>
        <Stack gap="sm">
          <UsageBar label="Channels" used={plan.usage.channels} limit={plan.limits.channels} />
          <UsageBar label="Assets" used={plan.usage.assets} limit={plan.limits.assets} />
          <UsageBar
            label="Publishes this period"
            used={plan.usage.publishes}
            kind="included"
            limit={
              plan.catalog.find((c) => c.tier === plan.tier)?.pricing.included.publishes ?? null
            }
          />
          <UsageBar
            label="Renders this period"
            used={plan.usage.renders}
            kind="included"
            limit={plan.catalog.find((c) => c.tier === plan.tier)?.pricing.included.renders ?? null}
          />
        </Stack>
        <p className="text-[11px] text-ink-muted">
          Channels and assets are hard caps for your tier — upgrade to raise them. Publishes and
          renders are never blocked: usage past the included amount is billed as overage.
        </p>
      </Stack>
    </Card>
  );
}

export function BillingPage() {
  const { workspaceId = '' } = useParams();
  const [params] = useSearchParams();
  const usage = useUsage(workspaceId);
  const checkout = useStartCheckout(workspaceId);
  const plan = usage.data?.plan;

  const choose = (tier: PlanTierName): void => {
    checkout.mutate(tier, {
      onSuccess: ({ url }) => {
        globalThis.location.href = url;
      },
    });
  };

  const checkoutState = params.get('checkout');

  return (
    <Page
      title="Billing"
      icon={DocumentIcon}
      description="Your plan, what you're using, this period's projected bill, and self-serve upgrades through Stripe."
      stats={
        plan !== undefined ? (
          <StatStrip
            stats={[
              {
                icon: LayersIcon,
                label: 'Channels used',
                value: plan.usage.channels,
                tone: 'accent',
              },
              { icon: DocumentIcon, label: 'Assets', value: plan.usage.assets },
              { icon: TrendIcon, label: 'This period', value: money(plan.bill.total) },
            ]}
          />
        ) : undefined
      }
    >
      <Stack gap="lg">
        {checkoutState === 'success' && (
          <Alert tone="success" title="Subscription updated">
            Thanks — your plan change is being confirmed by Stripe and will apply within moments.
          </Alert>
        )}
        {checkoutState === 'cancelled' && (
          <Alert tone="info" title="Checkout cancelled">
            No change was made to your plan.
          </Alert>
        )}
        {checkout.error instanceof Error && (
          <Alert tone="warning" title="Couldn't start checkout">
            {checkout.error.message}
          </Alert>
        )}

        <QueryState loading={usage.isLoading} error={usage.error} what="your plan">
          {plan !== undefined && (
            <Stack gap="lg">
              <CurrentPlan plan={plan} workspaceId={workspaceId} />
              <div>
                <p className="mb-3 font-heading text-lg text-navy-700">Plans</p>
                <Grid min="md" gap="md">
                  {plan.catalog.map((entry) => (
                    <PlanCard
                      key={entry.tier}
                      entry={entry}
                      current={plan.tier}
                      onChoose={() => choose(entry.tier)}
                      busy={checkout.isPending && checkout.variables === entry.tier}
                    />
                  ))}
                  <EnterpriseCard />
                </Grid>
              </div>
            </Stack>
          )}
        </QueryState>
      </Stack>
    </Page>
  );
}
